import type { KeyboardEvent, ReactNode } from "react";

import { IconButton } from "../../../IconButton";
import { ChevronIcon } from "../../../../icons/glyphs/ChevronIcon";
import type { CalendarDayCell } from "../useCalendarMonth";
import {
  Grid,
  GridSurface,
  Header,
  NavGroup,
  NavSlot,
  Pane,
  PaneHeader,
  Week,
  WeekdayCell,
  WeekdayRow,
} from "./calendarStyles";

export interface CalendarPaneProps {
  paneIndex: number;
  dual: boolean;
  /** 이 pane의 연/월 nav(`CalendarNav`). */
  nav: ReactNode;
  onPrev: () => void;
  onNext: () => void;
  weeks: CalendarDayCell[][];
  weekdayLabels: string[];
  slideDir: 1 | -1;
  /** grid role=grid의 접근성 이름. */
  gridAriaLabel?: string;
  /** 월 바뀌면 grid remount로 slide-in 재생하기 위한 key. */
  monthKey: string;
  onGridKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  renderCell: (cell: CalendarDayCell) => ReactNode;
}

/**
 * 한 pane(달) 레이아웃(RFC-date-picker) — 헤더(nav + 이전/다음) + 요일 라벨 + 6×7 day 그리드. day 셀 상태
 * 판정은 조합자가 `renderCell`로 주입하고, 이 컴포넌트는 배치만 책임진다. dual은 좌 pane ‹ / 우 pane ›
 * (두 달 함께 이동), 단일은 nav + ‹›.
 */
export const CalendarPane = ({
  paneIndex,
  dual,
  nav,
  onPrev,
  onNext,
  weeks,
  weekdayLabels,
  slideDir,
  gridAriaLabel,
  monthKey,
  onGridKeyDown,
  renderCell,
}: CalendarPaneProps) => {
  const prevBtn = (
    <IconButton size="sm" variant="ghost" aria-label="Previous month" onClick={onPrev}>
      <ChevronIcon style={{ transform: "rotate(90deg)" }} />
    </IconButton>
  );
  const nextBtn = (
    <IconButton size="sm" variant="ghost" aria-label="Next month" onClick={onNext}>
      <ChevronIcon style={{ transform: "rotate(-90deg)" }} />
    </IconButton>
  );

  // 단일: [연 월] ... [‹][›]  /  dual: 좌 pane [‹][연 월][spacer], 우 pane [spacer][연 월][›]
  const header = dual ? (
    <PaneHeader>
      <NavSlot>{paneIndex === 0 ? prevBtn : null}</NavSlot>
      {nav}
      <NavSlot $align="end">{paneIndex === 1 ? nextBtn : null}</NavSlot>
    </PaneHeader>
  ) : (
    <Header>
      {nav}
      <NavGroup>
        {prevBtn}
        {nextBtn}
      </NavGroup>
    </Header>
  );

  return (
    <Pane>
      {header}
      {/* 그리드는 recessed surface 패널에 담아 흰 팝업과 배경을 구분한다(§3). */}
      <GridSurface>
        <WeekdayRow aria-hidden="true">
          {weekdayLabels.map((wl, i) => (
            <WeekdayCell key={i}>{wl}</WeekdayCell>
          ))}
        </WeekdayRow>
        {/* key로 월이 바뀌면 remount되어 방향별 slide-in 재생($dir: +1 오른쪽에서/-1 왼쪽에서). */}
        <Grid
          key={monthKey}
          $dir={slideDir}
          role="grid"
          aria-label={gridAriaLabel}
          onKeyDown={onGridKeyDown}
        >
          {weeks.map((week, wi) => (
            <Week role="row" key={wi}>
              {week.map((cell) => renderCell(cell))}
            </Week>
          ))}
        </Grid>
      </GridSurface>
    </Pane>
  );
};
